import { useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminSupport } from '../../store/chat/chatActions';
import { getImage } from '../../utils';
import { SocketContext } from '../../utils/socketConfig';

function chatSortTime(chat) {
	const u = chat?.updatedAt ? new Date(chat.updatedAt).getTime() : 0;
	const m = chat?.message?.createdAt ? new Date(chat.message.createdAt).getTime() : 0;
	return Math.max(u, m);
}

const SupportLatestWidget = () => {
	const dispatch = useDispatch();
	const { chats } = useSelector((state) => state.chat);
	const socket = useContext(SocketContext);

	useEffect(() => {
		dispatch(getAdminSupport());
	}, [dispatch]);

	useEffect(() => {
		if (socket == null) return undefined;
		const handler = () => dispatch(getAdminSupport());
		socket.on('message', handler);
		return () => {
			socket.off('message', handler);
		};
	}, [socket, dispatch]);

	const latest = useMemo(() => {
		if (!Array.isArray(chats) || chats.length === 0) return null;
		return [...chats].sort((a, b) => chatSortTime(b) - chatSortTime(a))[0];
	}, [chats]);

	const preview = useMemo(() => {
		if (!latest?.message) return 'No messages yet';
		if (latest.message.isFile) return 'File message';
		return latest.message.message || '—';
	}, [latest]);

	const name = useMemo(() => {
		if (!latest?.user) return 'Support';
		const u = latest.user;
		return [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || 'User';
	}, [latest]);

	return (
		<div className="flex flex-col h-full min-h-[200px]">
			<h2 className="text-nt-blue max-h-[50px] mb-3">Support</h2>
			{latest ? (
				<div className="flex flex-1 flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50/80 p-4">
					<div className="flex items-start gap-3">
						<img
							className="h-10 w-10 shrink-0 rounded-full object-cover"
							src={getImage(latest.user?.avatar, 'AVATAR')}
							alt=""
						/>
						<div className="min-w-0 flex-1">
							<div className="font-medium text-slate-900 capitalize truncate">{name}</div>
							<p className="mt-1 line-clamp-3 text-sm text-slate-600 break-words">{preview}</p>
						</div>
					</div>
					<Link
						to="/admin/support"
						className="mt-auto text-center text-sm font-medium text-nt-blue hover:underline"
					>
						Open support inbox
					</Link>
				</div>
			) : (
				<p className="text-sm text-slate-500">No support conversations yet.</p>
			)}
		</div>
	);
};

export { SupportLatestWidget };
